import type { AuditRecord } from "./audit.types";

export interface AuditValues {
  /** Valor antes da alteração; vazio no primeiro preenchimento (vazio → valor, HU 834). */
  readonly anterior: string;
  /** Valor depois da alteração; vazio quando só o anterior existe (exclusão). */
  readonly novo: string;
  /** Perfil excluído: o valor anterior é o que deixou de existir e aparece riscado no detalhe. */
  readonly anteriorRiscado: boolean;
}

const STATUS_LABELS: Record<string, string> = { ATIVO: "Ativo", INATIVO: "Inativo" };

const DATE_LIKE = /^(\d{2}\/\d{2}\/\d{4}|\d{4}-\d{2}-\d{2})/;

/** Ação do log único por lote de anexos da ficha financeira (a lista mostra só o nome; o detalhe, a obra e a ficha, e os arquivos no registro alterado). */
export const AUDIT_ANEXO_INCLUIDO = "FICHA_FINANCEIRA_ANEXO_INCLUIDO";

/** Ação do log da geração da ficha financeira (a lista mostra só o nome; o detalhe, um dado por linha). */
export const AUDIT_FICHA_GERADA = "FICHA_FINANCEIRA_GERADA";

const FICHA_GERADA_CAMPOS: ReadonlyArray<readonly [string, string]> = [
  ["clienteFinal", "Cliente final"],
  ["contrato", "Contrato"],
  ["diretorProjeto", "Diretor de projeto"],
  ["responsavelPreparacao", "Responsável pela preparação"],
  ["responsavelExecucao", "Responsável pela execução"],
  ["coordenador", "Coordenador"],
];

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const text = (value: unknown): string => (typeof value === "string" ? value.trim() : "");

/**
 * Novo valor do log de anexos: a obra (código e embarcação; sem eles, o id da obra) e o id da ficha. Os arquivos aparecem
 * no registro alterado (nome do registro). Texto que não é o JSON esperado volta como veio.
 */
function formatAnexoIncluido(value: string): string {
  let parsed: unknown;
  try {
    parsed = JSON.parse(value);
  } catch {
    return value;
  }
  if (!isRecord(parsed)) return value;

  const fichaId = text(parsed.fichaId);
  const obra =
    [text(parsed.obraCodigo), text(parsed.embarcacao)].filter(Boolean).join(" — ") ||
    text(parsed.obraId);
  if (!obra && !fichaId) return value;

  return [obra ? `Obra: ${obra}` : "", fichaId ? `FichaId: ${fichaId}` : ""]
    .filter(Boolean)
    .join("\n");
}

function formatStatus(value: string): string {
  return STATUS_LABELS[value.trim().toUpperCase()] ?? value;
}

/** Identificação da obra do log: código e embarcação; sem eles (log antigo), o código do registro alterado e só então o id. */
function linhaObra(parsed: Record<string, unknown>, codigoRegistro = ""): string {
  const obra =
    [text(parsed.obraCodigo), text(parsed.embarcacao)].filter(Boolean).join(" — ") ||
    codigoRegistro ||
    text(parsed.obraId);
  return obra ? `Obra: ${obra}` : "";
}

/**
 * Novo valor do log da geração: a obra e os campos informados, um por linha, com "-"
 * no que ficou vazio. Texto que não é o JSON esperado volta como veio.
 */
function formatFichaGerada(value: string, codigoRegistro: string): string {
  let parsed: unknown;
  try {
    parsed = JSON.parse(value);
  } catch {
    return value;
  }
  if (!isRecord(parsed)) return value;

  const campos = FICHA_GERADA_CAMPOS.map(
    ([chave, rotulo]) => `${rotulo}: ${text(parsed[chave]) || "-"}`,
  );

  return [linhaObra(parsed, codigoRegistro), ...campos].filter(Boolean).join("\n");
}

/**
 * Valores do registro prontos para a tela (HU 744, 834 e 1555): o nome do perfil excluído ocupa o valor anterior, a data
 * de exclusão que o serviço manda como novo valor sai (já está em Data/Hora) e o status deixa de vir em caixa alta.
 */
export function resolveAuditValues(
  record: Pick<AuditRecord, "acao" | "valorAnterior" | "valorNovo" | "registroAlteradoNome"> &
    Partial<Pick<AuditRecord, "registroAlteradoCodigo">>,
): AuditValues {
  const excluido = record.acao === "PERFIL_EXCLUIDO";
  const isStatus = record.acao === "USUARIO_STATUS_ATUALIZADO";
  const anexoIncluido = record.acao === AUDIT_ANEXO_INCLUIDO;
  const fichaGerada = record.acao === AUDIT_FICHA_GERADA;
  const format = (value: string) =>
    isStatus
      ? formatStatus(value)
      : anexoIncluido
        ? formatAnexoIncluido(value)
        : fichaGerada
          ? formatFichaGerada(value, record.registroAlteradoCodigo ?? "")
          : value;

  const anterior = format(record.valorAnterior || (excluido ? record.registroAlteradoNome : ""));
  const novo = excluido && DATE_LIKE.test(record.valorNovo) ? "" : format(record.valorNovo);

  return {
    anterior,
    novo,
    anteriorRiscado: excluido && anterior !== "",
  };
}
